import React, { useState, useEffect } from 'react';
import { WRAP_TEXT } from '../shared/wrapText';
import { useTheme } from '../ThemeContext';
import { supabase } from '../supabaseClient';
import { homeownerPoolRow } from '../shared/planLimits';
import { formatPhoneDisplay, phoneTelHref } from '../shared/phoneFormat';
import AgentHome from './AgentHome';
import HomeMoveNotices from './HomeMoveNotices';
import { AgentPastDueNotice } from './AgentPastDueNotice';
import { homePlanBanner, loadAgentActivity, summarizeAgentActivity } from '../shared/agentActivity';
import { agentPost } from '../utils/functionAuth';
import { createPortalSession } from '../utils/stripe';
import { showToast } from './Toast';
import { isNativeApp } from '../utils/platform';

const Dashboard = ({ profile, onNavigate, activityOverride }) => {
  const { colors, isDark } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [poolUsage, setPoolUsage] = useState(null);
  const [period, setPeriod] = useState('month');
  const [activity, setActivity] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestError, setRequestError] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!profile?.id || typeof supabase.rpc !== 'function') return undefined;
    let cancelled = false;
    supabase.rpc('homeowner_pool_usage').then((result) => {
      if (cancelled || !result || result.error) return;
      const row = homeownerPoolRow(result.data);
      if (row) setPoolUsage(row);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [profile?.id, profile?.team_id]);

  useEffect(() => {
    if (!profile?.id) return undefined;
    let cancelled = false;
    loadAgentActivity(supabase, profile.id, {
      fetchProtected: () => agentPost('/.netlify/functions/agent-home-activity', {}),
    }).then((rows) => {
      if (!cancelled) setActivity(rows);
    }).catch(() => {
      if (!cancelled) setActivity({ clicks: [], clients: [], tasks: [], projects: [], reports: [], shares: [], transfers: [] });
    });
    return () => {
      cancelled = true;
    };
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.id) return undefined;
    let cancelled = false;
    agentPost('/.netlify/functions/agent-join-requests', { action: 'list' }).then((result) => {
      if (cancelled || !result || !result.ok) return;
      const rows = result.data && result.data.requests;
      setRequests(Array.isArray(rows) ? rows : []);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [profile?.id]);

  const decideRequest = async (id, action) => {
    setRequestError('');
    const result = await agentPost('/.netlify/functions/agent-join-requests', { action, id });
    if (!result || !result.ok) {
      const message = (result && result.data && result.data.error) || (result && result.error) || 'Could not update that request.';
      setRequestError(message);
      return;
    }
    setRequests((current) => current.filter((row) => row.id !== id));
  };

  return (
    <div>
      <h2 data-tour="dashboard" style={{...styles.pageTitle, color: colors.text, marginTop: isMobile ? 0 : undefined, marginBottom: isMobile ? '1rem' : '1.5rem'}}>
        Welcome back, {profile?.first_name}!
      </h2>

      <HomeMoveNotices profile={profile} colors={colors} />

      <AgentPastDueNotice
        profile={profile}
        colors={colors}
        isDark={isDark}
        onUpdateCard={async () => {
          if (isNativeApp()) return;
          if (!profile || !profile.stripe_customer_id) {
            if (onNavigate) onNavigate('settings');
            return;
          }
          try {
            await createPortalSession(profile.stripe_customer_id);
          } catch (error) {
            showToast('Could not open billing. Try again from Subscription & Billing.');
          }
        }}
      />

      <AgentHome
        activity={summarizeAgentActivity(activityOverride || activity, period)}
        period={period}
        onPeriod={setPeriod}
        banner={homePlanBanner(profile, poolUsage)}
        onBilling={() => {
          if (typeof window !== 'undefined') window.location.hash = '#billing';
          if (onNavigate) onNavigate('settings');
        }}
        onAddCustomer={() => onNavigate && onNavigate('clients')}
        onAddVendor={() => onNavigate && onNavigate('vendors')}
        onOpenRecord={(row) => onNavigate && onNavigate(row && row.kind === 'vendor' ? 'vendors' : 'clients')}
        initialOpen=""
        requests={requests}
        requestError={requestError}
        onAcceptRequest={(id) => decideRequest(id, 'accept')}
        onDeclineRequest={(id) => decideRequest(id, 'decline')}
      />

      {/* Agent Information Section */}
      <div style={{
        ...styles.brandingCard,
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        marginTop: '2rem'
      }}>
        <h3 style={{...styles.sectionTitle, color: colors.text, marginBottom: '1.5rem'}}>
          Your Information
        </h3>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '2rem',
          alignItems: isMobile ? 'stretch' : 'flex-start'
        }}>
          {/* Profile Photo Section */}
          <div style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            {/* Headshot */}
            <div style={{
              width: isMobile ? '120px' : '150px',
              height: isMobile ? '120px' : '150px',
              borderRadius: '50%',
              background: profile?.headshot_url ? 'transparent' : 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: 'white',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(89, 196, 196, 0.3)',
              overflow: 'hidden',
              border: profile?.headshot_url ? `3px solid ${colors.border}` : 'none'
            }}>
              {profile?.headshot_url ? (
                <img
                  src={profile.headshot_url}
                  alt={`${profile.first_name} ${profile.last_name}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; background: linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%); font-size: 3rem; color: white; font-weight: bold;">${profile?.first_name?.[0]}${profile?.last_name?.[0]}</div>`;
                  }}
                />
              ) : (
                `${profile?.first_name?.[0]}${profile?.last_name?.[0]}`
              )}
            </div>

            {/* Brokerage Logo */}
            {profile?.logo_url && (
              <div style={{
                width: isMobile ? '100px' : '120px',
                height: isMobile ? '60px' : '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                background: colors.surface,
                borderRadius: isMobile ? '0.75rem' : '0.5rem',
                border: `2px solid ${colors.border}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
              }}>
                <img
                  src={profile.logo_url}
                  alt={profile.brokerage || 'Brokerage'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            <button
              style={{
                ...styles.editBtn,
                background: colors.surfaceAlt,
                color: colors.text,
                border: `2px solid ${colors.border}`
              }}
              onClick={() => onNavigate('profile')}
            >
              Edit Profile
            </button>
          </div>

          {/* Branding Details */}
          <div style={{flex: 1, minWidth: 0}}>
            <div style={{marginBottom: '1.5rem'}}>
              <h4 style={{
                color: colors.text,
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}>
                {profile?.first_name} {profile?.last_name}
              </h4>
              <p style={{
                color: colors.textSecondary,
                fontSize: '1rem',
                marginBottom: '0.25rem',
                ...WRAP_TEXT
              }}>
                {profile?.email}
              </p>
              {profile?.phone && (
                phoneTelHref(profile.phone) ? (
                  <a href={phoneTelHref(profile.phone)} style={{
                    color: colors.textSecondary,
                    fontSize: '1rem',
                    textDecoration: 'none',
                  }}>
                    {formatPhoneDisplay(profile.phone)}
                  </a>
                ) : (
                  <p style={{
                    color: colors.textSecondary,
                    fontSize: '1rem',
                    margin: 0,
                  }}>
                    {profile.phone}
                  </p>
                )
              )}
            </div>

            {/* Agent Website */}
            {profile?.website && (
              <div style={{
                padding: '1.25rem',
                background: colors.surfaceAlt,
                borderRadius: isMobile ? '1.25rem' : '0.75rem',
                border: `1px solid ${colors.border}`,
                marginBottom: '1.5rem'
              }}>
                <h5 style={{
                  color: colors.text,
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Website
                </h5>
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#59c4c4',
                    fontSize: '1rem',
                    textDecoration: 'none',
                    fontWeight: '500',
                    ...WRAP_TEXT
                  }}
                >
                  {profile.website}{' '}
                  <span style={{fontSize: '0.8rem'}}>→</span>
                </a>
              </div>
            )}

            {/* Bio/About */}
            {profile?.bio && (
              <div style={{
                padding: '1.25rem',
                background: colors.surfaceAlt,
                borderRadius: isMobile ? '1.25rem' : '0.75rem',
                border: `1px solid ${colors.border}`
              }}>
                <h5 style={{
                  color: colors.text,
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  About
                </h5>
                <p style={{
                  color: colors.textSecondary,
                  fontSize: '1rem',
                  lineHeight: '1.6'
                }}>
                  {profile.bio}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageTitle: {
    fontSize: 'clamp(1.5rem, 5vw, 2rem)',
    fontWeight: '600'
  },
  sectionTitle: {
    fontSize: 'clamp(1.1rem, 4vw, 1.3rem)',
    fontWeight: '600'
  },
  brandingCard: {
    padding: window.innerWidth < 768 ? '1.5rem' : '2rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  editBtn: {
    padding: window.innerWidth < 768 ? '0.75rem 1.5rem' : '0.65rem 1.25rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '0.5rem',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: window.innerWidth < 768 ? '0.9rem' : '0.85rem',
    minHeight: window.innerWidth < 768 ? '44px' : 'auto',
    transition: 'all 0.2s'
  }
};

export default Dashboard;
